import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, MapPin, FolderOpen, Activity, Clock, Share2, Users, LogIn } from "lucide-react";
import { colors } from "../theme";
import { useAuth } from "../store/useAuth";
import { useStudio } from "../store/useStudio";
import { apiFetch } from "../api/client";
import { projectsApi } from "../api/projects";
import CollaborationShare from "./CollaborationShare";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const EVENT_LABEL = {
  session_start: "Session started", model_upload: "Uploaded a building model", scenario_create: "Created a scenario",
  scenario_save: "Saved a scenario", crops_update: "Updated crops", scenario_compare: "Compared scenarios",
  data_export: "Exported data", data_import: "Imported data", user_login: "Signed in", user_register: "Created account",
};

export default function ProfilePanel() {
  const { profileOpen, closeProfile, user, updateProfile } = useAuth();
  const loadScenario = useStudio((st) => st.loadScenario);
  const [data, setData] = useState(null);
  const [loc, setLoc] = useState("");
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState([]);
  const [shareProject, setShareProject] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joinMsg, setJoinMsg] = useState("");

  const loadShared = () => projectsApi.listShared().then(setShared).catch(() => setShared([]));

  useEffect(() => {
    if (profileOpen) {
      setLoc(user?.location || "");
      apiFetch("/api/me/activity").then(setData).catch(() => setData({ events: [], sessions: [], projects: [] }));
      loadShared();
    }
  }, [profileOpen, user]);

  if (!profileOpen || !user) return null;

  const openProject = async (id) => {
    try {
      const p = await projectsApi.get(id);
      loadScenario(p);
      closeProfile();
    } catch { setJoinMsg("Could not open that project."); }
  };
  const joinByCode = async () => {
    setJoinMsg("");
    const code = joinCode.trim().replace(/^.*join=/, "");
    if (!code) return;
    try {
      const r = await projectsApi.join(code);
      setJoinCode("");
      await loadShared();
      await openProject(r.id);
    } catch { setJoinMsg("Invalid or expired invite link."); }
  };

  return createPortal(
    <div style={s.overlay} data-testid="profile-panel">
      <div style={s.card}>
        <button style={s.close} onClick={closeProfile} data-testid="profile-close"><X size={18} /></button>
        <div style={s.head}>
          <div style={s.avatar}>{user.picture ? <img src={user.picture} alt="" style={s.avatarImg} /> : (user.name || user.email)[0].toUpperCase()}</div>
          <div>
            <div style={s.name}>{user.name}</div>
            <div style={s.email}>{user.email}</div>
            <div style={s.provider}>{user.provider === "google" ? "Google account" : "Email account"}{user.is_admin ? " · Admin" : ""}</div>
          </div>
        </div>

        <div style={s.editRow}>
          <MapPin size={14} style={{ color: colors.textTertiary }} />
          <input style={s.input} value={loc} placeholder="Your location" onChange={(e) => setLoc(e.target.value)} data-testid="profile-location" />
          <button style={s.saveBtn} data-testid="profile-save"
            onClick={async () => { await updateProfile({ location: loc }); setSaved(true); setTimeout(() => setSaved(false), 1500); }}>
            {saved ? "Saved" : "Save"}
          </button>
        </div>

        <div style={s.stats}>
          <Stat icon={<FolderOpen size={15} />} n={data?.projects?.length ?? "–"} label="Saved projects" />
          <Stat icon={<Activity size={15} />} n={data?.events?.length ?? "–"} label="Recent actions" />
          <Stat icon={<Clock size={15} />} n={data?.sessions?.length ?? "–"} label="Sessions" />
        </div>

        <div style={s.sectionTitle}>Saved projects</div>
        <div style={s.list} data-testid="profile-projects">
          {(data?.projects || []).length === 0 && <div style={s.empty}>No saved projects yet. Save a scenario to sync it across devices.</div>}
          {(data?.projects || []).map((p) => (
            <div key={p.id} style={s.row}>
              <span style={{ ...s.rowMain, flex: 1, cursor: p.id ? "pointer" : "default" }} onClick={() => p.id && openProject(p.id)} data-testid={`profile-open-${p.id}`}>
                {p.name || p.project_name || "Untitled"}
                {(p.collaborators || []).length > 0 && <span style={s.collabTag}><Users size={10} /> {(p.collaborators || []).length}</span>}
              </span>
              <span style={s.rowSub}>{fmt(p.updated_at || p.created_at)}</span>
              {p.id && <button style={s.shareBtn} onClick={() => setShareProject({ id: p.id, name: p.name || p.project_name })} data-testid={`profile-share-${p.id}`}><Share2 size={13} /> Share</button>}
            </div>
          ))}
        </div>

        <div style={s.sectionTitle}>Shared with me</div>
        <div style={s.list} data-testid="profile-shared">
          {shared.length === 0 && <div style={s.empty}>No projects shared with you yet. Ask a teammate to invite your email, or paste an invite link below.</div>}
          {shared.map((p) => (
            <div key={p.id} style={s.row}>
              <span style={{ ...s.rowMain, flex: 1, cursor: "pointer" }} onClick={() => openProject(p.id)} data-testid={`profile-shared-open-${p.id}`}>
                {p.name || "Untitled"}<span style={s.ownerTag}>{p.owner_email || "shared"}</span>
              </span>
              <span style={s.rowSub}>{fmt(p.updated_at)}</span>
            </div>
          ))}
        </div>
        <div style={s.joinRow}>
          <LogIn size={14} style={{ color: colors.textTertiary }} />
          <input style={s.input} value={joinCode} placeholder="Paste invite link or code" data-testid="profile-join-input"
            onChange={(e) => setJoinCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && joinByCode()} />
          <button style={s.saveBtn} onClick={joinByCode} data-testid="profile-join-btn">Join</button>
        </div>
        {joinMsg && <div style={{ ...s.empty, color: "#d9534f" }} data-testid="profile-join-msg">{joinMsg}</div>}


        <div style={s.sectionTitle}>Recent activity</div>
        <div style={s.list} data-testid="profile-activity">
          {(data?.events || []).length === 0 && <div style={s.empty}>No activity recorded yet.</div>}
          {(data?.events || []).slice(0, 20).map((e) => (
            <div key={e.id} style={s.row}><span style={s.rowMain}>{EVENT_LABEL[e.type] || e.type}</span><span style={s.rowSub}>{fmt(e.created_at)}</span></div>
          ))}
        </div>
      </div>
      {shareProject && <CollaborationShare project={shareProject} onClose={() => { setShareProject(null); apiFetch("/api/me/activity").then(setData).catch(() => {}); }} />}
    </div>, document.body);
}

const Stat = ({ icon, n, label }) => (
  <div style={s.stat}><div style={s.statIcon}>{icon}</div><div style={s.statN}>{n}</div><div style={s.statL}>{label}</div></div>
);

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,32,0.5)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4000 },
  card: { position: "relative", width: 460, maxWidth: "94vw", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 20, padding: "28px", boxShadow: "0 24px 70px rgba(0,0,0,0.28)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" },
  close: { position: "absolute", top: 14, right: 14, border: "none", background: "transparent", cursor: "pointer", color: colors.textTertiary },
  head: { display: "flex", gap: 14, alignItems: "center", marginBottom: 18 },
  avatar: { width: 54, height: 54, borderRadius: 999, background: colors.brand, color: "#fff", fontSize: 22, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  name: { fontSize: 18, fontWeight: 800, color: colors.textPrimary },
  email: { fontSize: 12.5, color: colors.textTertiary },
  provider: { fontSize: 10.5, color: colors.brand, fontWeight: 700, marginTop: 2 },
  editRow: { display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${colors.border}`, borderRadius: 11, padding: "0 11px", marginBottom: 16 },
  input: { flex: 1, border: "none", outline: "none", padding: "10px 0", fontSize: 13, color: colors.textPrimary },
  saveBtn: { border: "none", background: colors.brand, color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  stats: { display: "flex", gap: 10, marginBottom: 18 },
  stat: { flex: 1, background: colors.surfaceAlt || "#f6f8fa", borderRadius: 12, padding: "12px 10px", textAlign: "center" },
  statIcon: { color: colors.brand, display: "flex", justifyContent: "center", marginBottom: 4 },
  statN: { fontSize: 20, fontWeight: 800, color: colors.textPrimary },
  statL: { fontSize: 10.5, color: colors.textTertiary },
  sectionTitle: { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: colors.textTertiary, margin: "8px 0 8px" },
  list: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 },
  empty: { fontSize: 12, color: colors.textTertiary, fontStyle: "italic", padding: "8px 0" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "8px 11px", background: "#f6f8fa", borderRadius: 9 },
  rowMain: { fontSize: 12.5, fontWeight: 600, color: colors.textPrimary, display: "flex", alignItems: "center", gap: 6 },
  rowSub: { fontSize: 10.5, color: colors.textTertiary },
  shareBtn: { display: "inline-flex", alignItems: "center", gap: 4, border: `1px solid ${colors.border}`, background: "#fff", color: colors.textSecondary, borderRadius: 8, padding: "4px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer" },
  collabTag: { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color: "#2a6fd6", background: "#2a6fd612", borderRadius: 999, padding: "1px 7px" },
  ownerTag: { fontSize: 10, color: colors.textTertiary, marginLeft: 8 },
  joinRow: { display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${colors.border}`, borderRadius: 11, padding: "0 11px", marginTop: 4, marginBottom: 6 },
};
